import { useQueries } from '@tanstack/react-query'
import { FunctionComponent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import useAuth from '../../../../base/hooks/useAuth'
import { useUtils } from '../../../../base/hooks/useUtils.tsx'
import { ApiImpresoraPuntoVentaResponse, useImpresoraPuntoVenta } from '../api/apiImpresoraPuntoVenta.ts'
import {
  ImpresionAccionExtraContextProps,
  ImpresionDocumentoContext,
  ImpresionDocumentoContextValue,
  ImprimirDocumentoParams,
  MotivoImpresionProps,
  ResultadoImpresionProps,
  SeguimientoTrabajoProps,
} from '../hooks/useImpresionDocumento.ts'
import { IsiPrintDiagnostico, useIsiPrint } from '../hooks/useIsiPrint.ts'
import {
  IMPRESION_FORMATO,
  ImpresionDocumentoProps,
  ImpresionFormatoProps,
  ImpresoraPuntoVentaProps,
} from '../interfaces/impresora.ts'
import type { EscposDocument, JobInfo, PrintResult } from '../sdk/isi-printer-client.ts'
import { ISI_PRINT_SOPORTADO, MENSAJE_ISI_PRINT_NO_SOPORTADO } from '../services/dispositivo.ts'
import { getImpresionDocumentoLabel } from '../services/impresionDocumento.ts'
import { esFormatoRollo, esUrlLocal, pdfABlob, PdfFuenteProps } from '../services/impresionPdf.ts'
import {
  cancelarTrabajoIsiPrint,
  ISI_PRINTER_STATUS_LABEL,
  isiPrint,
  IsiPrintEnvioOpciones,
  mensajeIsiPrint,
  mensajeTrabajoIsiPrint,
  PrinterError,
  trabajoConProblema,
} from '../services/isiPrintClient.ts'
import ImpresionDocumentoDialog from './ImpresionDocumentoDialog'
import IsiPrintTrabajosAviso, { TrabajoAvisoItem } from './isiPrint/IsiPrintTrabajosAviso'

/** Estado del diálogo abierto por el provider */
interface DialogoState {
  params: ImprimirDocumentoParams
  pdf: PdfFuenteProps
  formato: ImpresionFormatoProps
  motivo?: MotivoImpresionProps
  mensaje?: string | null
  /** Impresora sugerida por el agente ante PRINTER_NOT_FOUND */
  sugerencia?: string | null
}

interface Resolucion {
  asignacion: ImpresoraPuntoVentaProps | null
  motivo?: MotivoImpresionProps
  mensaje?: string | null
  sugerencia?: string | null
}

/** Trabajo aceptado por isi-print que se sigue hasta que se imprima */
interface TrabajoSeguido extends SeguimientoTrabajoProps {
  jobId: string
  /** Momento en que se aceptó (para detectar trabajos que no avanzan) */
  desde: number
  /** El usuario cerró el aviso: se sigue, pero sin mostrarlo */
  oculto: boolean
}

/** Pasado este tiempo en cola sin imprimirse, se avisa aunque el agente no informe un motivo */
const SEGUIMIENTO_EN_COLA_MS = 20_000
/** Pasado este tiempo se deja de seguir el trabajo */
const SEGUIMIENTO_MAX_MS = 10 * 60_000
const KEY_ISI_PRINT_TRABAJO = 'isi-print-trabajo'

/** Motivo de diálogo a partir de un error del agente */
const motivoDeError = (error: unknown): MotivoImpresionProps => {
  if (error instanceof PrinterError) {
    if (error.code === 'DEVICE_NOT_SUPPORTED') return 'dispositivo-no-soportado'
    if (error.code === 'PRINTER_NOT_FOUND') return 'impresora-no-encontrada'
    if (error.code === 'ORIGIN_NOT_AUTHORIZED') return 'isi-print-no-autorizado'
    if (error.code?.startsWith('LICENSE_')) return 'sin-licencia'
    return 'error-impresion'
  }
  // fetch rechazado: el agente no está en ejecución
  if (error instanceof TypeError) return 'isi-print-no-instalado'
  return 'error-impresion'
}

/**
 * Provee `useImpresionDocumento`: consulta una vez por sesión las impresoras del punto de venta
 * (`impresorasPuntoVenta`, en caché de TanStack Query sin polling), verifica isi-print en este equipo
 * (instalación, autorización, licencia e impresoras), sigue cada trabajo enviado hasta que se imprima
 * y monta el diálogo de impresión. Debe ir dentro de los providers de Auth, Toast y Router.
 */
export interface ImpresionDocumentoProviderProps {
  children: ReactNode
  /**
   * Botones adicionales del diálogo de impresión, debajo de "Abrir en otra pestaña".
   * Así el proyecto agrega acciones (ej. enviar por WhatsApp) sin que este módulo dependa de ellas.
   * @example
   * <ImpresionDocumentoProvider accionesExtra={(ctx) => <WaapiImpresionAccion {...ctx} />}>
   */
  accionesExtra?: (ctx: ImpresionAccionExtraContextProps) => ReactNode
}

const ImpresionDocumentoProvider: FunctionComponent<ImpresionDocumentoProviderProps> = ({
  children,
  accionesExtra,
}) => {
  const { user } = useAuth()
  const { toast } = useUtils()
  const codigoSucursal = user?.sucursal?.codigo
  const codigoPuntoVenta = user?.puntoVenta?.codigo
  const haySesion = codigoSucursal != null && codigoPuntoVenta != null

  const query = useImpresoraPuntoVenta(
    { codigoSucursal: codigoSucursal ?? 0, codigoPuntoVenta: codigoPuntoVenta ?? 0 },
    {
      enabled: haySesion,
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnReconnect: false,
      retry: 1,
    },
  )
  const config = query.data ?? null

  // Al iniciar sesión: verifica isi-print e instala o renueva su licencia (ensureLicense)
  const isi = useIsiPrint({ habilitado: haySesion })

  // Refs para que las funciones expuestas sean estables y lean siempre el último estado
  const queryRef = useRef(query)
  queryRef.current = query
  const isiRef = useRef(isi)
  isiRef.current = isi
  const haySesionRef = useRef(haySesion)
  haySesionRef.current = haySesion

  const [dialogo, setDialogo] = useState<DialogoState | null>(null)

  /** Configuración en caché o, si aún no llegó, la espera */
  const obtenerConfig = useCallback(async (): Promise<ApiImpresoraPuntoVentaResponse | null> => {
    const q = queryRef.current
    if (q.data) return q.data
    if (!haySesionRef.current) return null
    const r = await q.refetch()
    return r.data ?? null
  }, [])

  const buscarAsignacion = (
    cfg: ApiImpresoraPuntoVentaResponse | null,
    documento?: ImpresionDocumentoProps | null,
  ) => (documento && cfg?.asignaciones?.find((a) => a.documento === documento)) || null

  /**
   * Decide si se puede imprimir directo: licencia, asignación, estado de isi-print y estado de la
   * impresora (con una verificación del agente de hace menos de 5 s).
   */
  const resolver = useCallback(
    async (
      cfg: ApiImpresoraPuntoVentaResponse | null,
      documento: ImpresionDocumentoProps,
    ): Promise<Resolucion> => {
      if (!cfg) return { asignacion: null, motivo: 'sin-configuracion' }
      const asignacion = buscarAsignacion(cfg, documento)
      if (!cfg.licencia) return { asignacion, motivo: 'sin-licencia', mensaje: cfg.mensaje }
      if (!asignacion?.nombreImpresora) return { asignacion: null, motivo: 'sin-asignacion' }

      const isiActual = isiRef.current
      let diag: IsiPrintDiagnostico | null = null
      try {
        diag = await isiActual.obtenerDiagnostico()
      } catch {
        diag = null
      }
      if (!diag || diag.estado === 'NO_INSTALADO') return { asignacion, motivo: 'isi-print-no-instalado' }
      if (diag.estado === 'PENDIENTE_AUTORIZAR') return { asignacion, motivo: 'isi-print-no-autorizado' }
      if (diag.estado === 'SIN_LICENCIA') return { asignacion, motivo: 'sin-licencia', mensaje: diag.detalle }
      if (isiActual.actualizacion.updateRequired) return { asignacion, motivo: 'isi-print-actualizar' }

      // Si no se pudo listar impresoras (diag.detalle) no se bloquea: el agente responderá al imprimir
      if (!diag.detalle) {
        const r = isiActual.estadoImpresora(asignacion.nombreImpresora, diag.impresoras)
        if (r.status === 'no-encontrada') return { asignacion, motivo: 'impresora-no-encontrada' }
        if (r.impresora && r.status !== 'online') {
          return {
            asignacion,
            motivo: 'impresora-no-disponible',
            mensaje: ISI_PRINTER_STATUS_LABEL[r.impresora.status],
          }
        }
      }
      return { asignacion }
    },
    [],
  )

  const enviarAImpresora = useCallback(
    async (
      pdf: PdfFuenteProps | null,
      printer: string,
      copias = 1,
      filename = 'documento.pdf',
      escpos?: EscposDocument | null,
      envio: IsiPrintEnvioOpciones = {},
    ): Promise<PrintResult> => {
      if (!ISI_PRINT_SOPORTADO) throw new PrinterError(MENSAJE_ISI_PRINT_NO_SOPORTADO, 'DEVICE_NOT_SUPPORTED')
      const isiActual = isiRef.current
      const n = Math.max(1, copias)
      if (escpos) {
        // ESC/POS no tiene copias: un trabajo por copia, cada uno con su propia clave anti-duplicado
        let r: PrintResult | null = null
        for (let i = 0; i < n; i++) {
          const key =
            envio.idempotencyKey && n > 1 ? `${envio.idempotencyKey}-${i + 1}` : envio.idempotencyKey
          r = await isiActual.printEscpos(printer, escpos, { ...envio, idempotencyKey: key })
        }
        return r!
      }
      if (!pdf) throw new Error('No hay documento para imprimir')
      // El agente no puede descargar URLs locales (blob:): se le envía el archivo
      if (pdf instanceof Blob || esUrlLocal(pdf)) {
        return isiActual.printFile(await pdfABlob(pdf), printer, { copies: n }, filename, envio)
      }
      return isiActual.printUrl(pdf, printer, { copies: n }, envio)
    },
    [],
  )

  const enviarDocumento = useCallback(
    async (
      params: ImprimirDocumentoParams,
      destino: { printer: string; copias?: number },
      formato: ImpresionFormatoProps,
      envio: IsiPrintEnvioOpciones = {},
    ) => {
      const escpos = params.obtenerEscpos && esFormatoRollo(formato) ? await params.obtenerEscpos() : null
      const pdf = escpos ? null : await params.obtenerPdf(formato)
      const resultado = await enviarAImpresora(
        pdf,
        destino.printer,
        destino.copias,
        params.filename,
        escpos,
        envio,
      )
      return { resultado, pdf }
    },
    [enviarAImpresora],
  )

  const abrirDialogo = useCallback(
    async (
      params: ImprimirDocumentoParams,
      resolucion: Resolucion,
      pdfPrevio?: { pdf: PdfFuenteProps; formato: ImpresionFormatoProps },
    ) => {
      const formato =
        pdfPrevio?.formato ??
        params.formato ??
        resolucion.asignacion?.formato ??
        params.formatoPorDefecto ??
        IMPRESION_FORMATO.ROLLO
      const pdf = pdfPrevio?.pdf ?? (await params.obtenerPdf(formato))
      setDialogo({
        params,
        pdf,
        formato,
        motivo: resolucion.motivo,
        mensaje: resolucion.mensaje,
        sugerencia: resolucion.sugerencia,
      })
    },
    [],
  )

  // ------------------------------------------------------------ seguimiento de trabajos

  const [trabajos, setTrabajos] = useState<TrabajoSeguido[]>([])
  const [ocupadoTrabajo, setOcupadoTrabajo] = useState<string | null>(null)

  // Re-render periódico mientras haya trabajos: el aviso "sigue en cola" depende del tiempo transcurrido
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!trabajos.length) return
    const id = setInterval(() => setTick((n) => n + 1), 5_000)
    return () => clearInterval(id)
  }, [trabajos.length])

  const seguirTrabajo = useCallback((resultado: PrintResult, info: SeguimientoTrabajoProps) => {
    const jobId = resultado.job_id
    if (!jobId || resultado.duplicate || resultado.job?.status === 'printed') return
    setTrabajos((prev) =>
      prev.some((t) => t.jobId === jobId)
        ? prev
        : [...prev, { ...info, jobId, desde: Date.now(), oculto: false }],
    )
  }, [])

  const quitarTrabajo = useCallback(
    (jobId: string) => setTrabajos((prev) => prev.filter((t) => t.jobId !== jobId)),
    [],
  )

  // Un getJob por trabajo cada segundo; cada consulta se detiene sola al llegar a un estado final
  const estados = useQueries({
    queries: trabajos.map((t) => ({
      queryKey: [KEY_ISI_PRINT_TRABAJO, t.jobId],
      queryFn: () => isiPrint.getJob(t.jobId),
      refetchInterval: (q: { state: { data?: JobInfo } }) => (q.state.data?.final ? false : 1_000),
      refetchOnWindowFocus: false,
      retry: false,
      gcTime: 60_000,
    })),
  })
  const jobs = estados.map((e) => e.data)
  const firmaJobs = jobs.map((j) => (j ? `${j.id}:${j.status}:${j.reason ?? ''}:${j.final}` : '-')).join('|')

  /** Abre el diálogo con el documento de un trabajo que falló o que se va a imprimir en otra */
  const reabrirTrabajo = useCallback(
    async (t: TrabajoSeguido, mensaje: string) => {
      if (!t.params) return
      try {
        await abrirDialogo(
          t.params,
          {
            asignacion: buscarAsignacion(queryRef.current.data ?? null, t.params.documento),
            motivo: 'error-impresion',
            mensaje,
          },
          t.pdf ? { pdf: t.pdf, formato: t.formato ?? IMPRESION_FORMATO.ROLLO } : undefined,
        )
      } catch (e) {
        toast.error(`No se pudo abrir ${t.titulo}: ${mensajeIsiPrint(e)}`)
      }
    },
    [abrirDialogo, toast],
  )

  // Trabajos terminados: impresos se dejan de seguir; fallidos o cancelados reabren el diálogo
  useEffect(() => {
    trabajos.forEach((t, i) => {
      const job = jobs[i]
      if (Date.now() - t.desde > SEGUIMIENTO_MAX_MS) {
        quitarTrabajo(t.jobId)
        if (job && !job.final) toast.warning(`${t.titulo}: no se confirmó la impresión en ${t.impresora}`)
        return
      }
      if (!job?.final) return
      quitarTrabajo(t.jobId)
      if (job.status === 'printed') return
      const mensaje = `${t.impresora}: ${mensajeTrabajoIsiPrint(job)}`
      if (job.reason === 'CANCELLED_BY_CLIENT') return
      if (t.params) void reabrirTrabajo(t, mensaje)
      else toast.error(`${t.titulo} no se imprimió (${mensaje})`)
    })
    // firmaJobs resume los cambios de estado de los trabajos
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaJobs])

  const avisos: TrabajoAvisoItem[] = trabajos.flatMap((t, i) => {
    const job = jobs[i]
    if (t.oculto || !job || job.final) return []
    const detenido = trabajoConProblema(job)
    const enCola = job.status === 'queued' && Date.now() - t.desde > SEGUIMIENTO_EN_COLA_MS
    if (!detenido && !enCola) return []
    return [
      {
        jobId: t.jobId,
        titulo: t.titulo,
        impresora: t.impresora,
        motivo: detenido ? mensajeTrabajoIsiPrint(job) : 'sigue en cola',
        puedeReimprimir: !!t.params,
        cancelable: job.cancellable,
      },
    ]
  })

  const cancelarTrabajo = async (jobId: string, imprimirEnOtra: boolean) => {
    const t = trabajos.find((x) => x.jobId === jobId)
    if (!t) return
    setOcupadoTrabajo(jobId)
    try {
      const job = jobs[trabajos.indexOf(t)]
      // Cancelar evita que salga duplicado cuando la impresora vuelva
      const error = job?.cancellable ? await cancelarTrabajoIsiPrint(jobId) : null
      if (error === 'JOB_ALREADY_PRINTED') {
        toast.info(`${t.titulo} ya se imprimió`)
        quitarTrabajo(jobId)
        return
      }
      if (error && error !== 'JOB_ALREADY_FINISHED') {
        toast.error(`No se pudo cancelar ${t.titulo} (${error})`)
        return
      }
      quitarTrabajo(jobId)
      if (imprimirEnOtra) void reabrirTrabajo(t, `${t.impresora}: cancelado para imprimir en otra impresora`)
      else toast.info(`${t.titulo} cancelado en ${t.impresora}`)
    } finally {
      setOcupadoTrabajo(null)
    }
  }

  // ------------------------------------------------------------ impresión

  const imprimir = useCallback(
    async (params: ImprimirDocumentoParams): Promise<ResultadoImpresionProps> => {
      const titulo = params.titulo ?? getImpresionDocumentoLabel(params.documento)

      // Teléfonos y tabletas: sin isi-print. Lo automático no abre nada; lo pedido abre el menú móvil
      if (!ISI_PRINT_SOPORTADO) {
        if (params.automatico) return { impreso: false, motivo: 'dispositivo-no-soportado' }
        try {
          await abrirDialogo(params, { asignacion: null, motivo: 'dispositivo-no-soportado' })
        } catch (error) {
          toast.error(`No se pudo generar ${titulo}: ${mensajeIsiPrint(error)}`)
          return { impreso: false, error }
        }
        return { impreso: false, motivo: 'dispositivo-no-soportado' }
      }

      const cfg = await obtenerConfig().catch(() => null)
      const resolucion = await resolver(cfg, params.documento)

      try {
        if (resolucion.motivo) {
          await abrirDialogo(params, resolucion)
          return { impreso: false, motivo: resolucion.motivo }
        }

        const asignacion = resolucion.asignacion!
        const formato = params.formato ?? asignacion.formato
        const nombre = asignacion.impresora?.nombre || asignacion.nombreImpresora
        let pdf: PdfFuenteProps | null = null
        try {
          const envio = await enviarDocumento(
            params,
            { printer: asignacion.nombreImpresora, copias: asignacion.copias },
            formato,
            { idempotencyKey: params.idempotencyKey, force: params.reimpresion },
          )
          pdf = envio.pdf
          const r = envio.resultado
          if (r.duplicate) {
            toast.info(`${titulo} ya se envió a ${nombre} hace un momento; no se imprime de nuevo`)
            return { impreso: true }
          }
          if (r.job && trabajoConProblema(r.job) && r.job.final)
            throw new Error(mensajeTrabajoIsiPrint(r.job))
          toast.success(`${titulo} enviado a ${nombre}`)
          if (r.warning?.message) toast.info(r.warning.message)
          seguirTrabajo(r, { titulo, impresora: nombre, params, pdf, formato })
          return { impreso: true }
        } catch (error) {
          await abrirDialogo(
            params,
            {
              asignacion,
              motivo: motivoDeError(error),
              mensaje: mensajeIsiPrint(error),
              sugerencia: error instanceof PrinterError ? (error.suggestedMatch ?? null) : null,
            },
            pdf ? { pdf, formato } : undefined,
          )
          // La impresora o el agente pudieron cambiar: se vuelve a verificar
          void isiRef.current.refrescar()
          return { impreso: false, motivo: motivoDeError(error), error }
        }
      } catch (error) {
        // No se pudo obtener el PDF o el ticket
        toast.error(`No se pudo generar ${titulo}: ${mensajeIsiPrint(error)}`)
        return { impreso: false, error }
      }
    },
    [abrirDialogo, enviarDocumento, obtenerConfig, resolver, seguirTrabajo, toast],
  )

  const abrirVisor = useCallback(
    async (params: ImprimirDocumentoParams) => {
      const cfg = await obtenerConfig().catch(() => null)
      const asignacion = buscarAsignacion(cfg, params.documento)
      try {
        await abrirDialogo(params, { asignacion })
      } catch (error) {
        toast.error(`No se pudo generar el documento: ${mensajeIsiPrint(error)}`)
      }
    },
    [abrirDialogo, obtenerConfig, toast],
  )

  const obtenerAsignacion = useCallback(
    (documento?: ImpresionDocumentoProps | null) =>
      buscarAsignacion(queryRef.current.data ?? null, documento),
    [],
  )

  const value = useMemo<ImpresionDocumentoContextValue>(
    () => ({
      imprimir,
      abrirVisor,
      obtenerAsignacion,
      enviarAImpresora,
      enviarDocumento,
      seguirTrabajo,
      licencia: config ? config.licencia : null,
      mensaje: config?.mensaje ?? null,
      cargando: query.isLoading,
      accionesExtra,
    }),
    // config cambia la licencia y las asignaciones que lee obtenerAsignacion
    [
      imprimir,
      abrirVisor,
      obtenerAsignacion,
      enviarAImpresora,
      enviarDocumento,
      seguirTrabajo,
      config,
      query.isLoading,
      accionesExtra,
    ],
  )

  return (
    <ImpresionDocumentoContext.Provider value={value}>
      {children}
      {dialogo && (
        <ImpresionDocumentoDialog
          open
          titulo={dialogo.params.titulo}
          documento={dialogo.params.documento}
          pdf={dialogo.pdf}
          formato={dialogo.formato}
          obtenerPdf={dialogo.params.obtenerPdf}
          obtenerEscpos={dialogo.params.obtenerEscpos}
          filename={dialogo.params.filename}
          motivo={dialogo.motivo}
          mensaje={dialogo.mensaje}
          sugerencia={dialogo.sugerencia}
          onImpreso={dialogo.params.onImpreso}
          destinatario={dialogo.params.destinatario}
          whatsapp={dialogo.params.whatsapp}
          onClose={() => setDialogo(null)}
        />
      )}
      <IsiPrintTrabajosAviso
        items={avisos}
        ocupado={ocupadoTrabajo}
        onCancelar={(id) => void cancelarTrabajo(id, false)}
        onImprimirEnOtra={(id) => void cancelarTrabajo(id, true)}
        onOcultar={(id) =>
          setTrabajos((prev) => prev.map((t) => (t.jobId === id ? { ...t, oculto: true } : t)))
        }
      />
    </ImpresionDocumentoContext.Provider>
  )
}

export default ImpresionDocumentoProvider
